import { useCallback, useMemo, type ReactNode } from 'react';
import {
  MsalProvider,
  useIsAuthenticated,
  useMsal,
  type MsalAuthenticationResult,
} from '@azure/msal-react';
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { AuthContextProvider, type AuthAccount, type AuthState } from './AuthContext.js';
import { apiScopes, buildMsalConfiguration, type ResolvedMsalConfig } from './msalConfig.js';

export interface MsalAuthProviderProps {
  config: ResolvedMsalConfig;
  children: ReactNode;
}

export function MsalAuthProvider({ config, children }: MsalAuthProviderProps) {
  const instance = useMemo(
    () => new PublicClientApplication(buildMsalConfiguration(config)),
    [config],
  );
  return (
    <MsalProvider instance={instance}>
      <MsalBridge config={config}>{children}</MsalBridge>
    </MsalProvider>
  );
}

function MsalBridge({ config, children }: { config: ResolvedMsalConfig; children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const account: AuthAccount | null = useMemo(() => {
    const a = accounts[0];
    if (!a) return null;
    return {
      email:
        (typeof a.username === 'string' && a.username.length > 0 ? a.username : '') ||
        ((a.idTokenClaims as { email?: string } | undefined)?.email ?? ''),
      name: a.name ?? '',
    };
  }, [accounts]);

  const signIn = useCallback(async () => {
    // `prompt: 'select_account'` forces the Microsoft account picker even when
    // the browser already has a signed-in MS account cached (e.g. a school/work
    // tenant from another app). Without it, MSAL silently reuses that account.
    await instance.loginRedirect({ scopes: apiScopes(config), prompt: 'select_account' });
  }, [instance, config]);

  const signOut = useCallback(async () => {
    const a = accounts[0];
    await instance.logoutRedirect(a ? { account: a } : {});
  }, [instance, accounts]);

  const getToken = useCallback(async (): Promise<string | null> => {
    const a = accounts[0];
    if (!a) return null;
    try {
      const result: AuthenticationResult = await instance.acquireTokenSilent({
        account: a,
        scopes: apiScopes(config),
      });
      // Send the ID token (aud=clientId) as the bearer — matches the rest of
      // the SM2GK fleet. The access token would have aud=Microsoft Graph and
      // would not validate against our API.
      return result.idToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ scopes: apiScopes(config) });
        return null;
      }
      throw err;
    }
  }, [instance, accounts, config]);

  const status: AuthState['status'] =
    inProgress !== 'none' ? 'loading' : isAuthenticated ? 'authenticated' : 'unauthenticated';

  const value: AuthState = useMemo(
    () => ({ status, account, signIn, signOut, getToken }),
    [status, account, signIn, signOut, getToken],
  );

  return <AuthContextProvider value={value}>{children}</AuthContextProvider>;
}

// Re-export the MSAL result type for any callers that need it.
export type { MsalAuthenticationResult };
